/**
 * IR → laned graph layout. The load-bearing algorithm the flagship exists to de-risk.
 *
 * Model (workflow-designer.md §4.2, §4.6):
 *  - The 8 lifecycle stages are vertical swimlanes, fixed left→right order (time-reads-right).
 *  - A node lives in exactly one lane (its `stage`).
 *  - Within the graph, a node's ROW is its longest-path topological rank over CONTROL edges only
 *    (sequence + status). Data edges are excluded — "the data layer never gates" (§4.5), so a data
 *    back-edge (the corpus dependentOn mutual clique) is legal and never affects ordering.
 *  - Siblings sharing a lane+rank spread into sub-columns; a lane widens to fit its widest rank.
 *  - ALL 8 lanes are always rendered; empty lanes carry a teaching empty-state (§4.2).
 *
 * Deterministic and dependency-free. PS-2 swaps in elkjs for edge-routing refinement; the lane +
 * rank assignment here is the semantic layout and stays authoritative.
 */
import { LIFECYCLE_STAGES, type LifecycleStage, type StageMeta } from '@/theme/tokens';
import { isControlEdge, type FlowDefinition, type FlowNode } from './types';

export interface LaneGeometry {
  stage: StageMeta;
  x: number;
  width: number;
  nodeCount: number;
  isGate: boolean;
}

export interface LaidOutNode {
  node: FlowNode;
  x: number;
  y: number;
  rank: number;
  /** Sub-column index within its lane+rank group (for stable ordering). */
  col: number;
}

export interface FlowLayout {
  nodes: LaidOutNode[];
  lanes: LaneGeometry[];
  width: number;
  height: number;
}

const NODE_W = 210;
const NODE_H = 90;
const ROW_H = 132;
const H_GAP = 30;
const INNER_PAD = 24;
const BASE_LANE_W = 300;
const GATE_LANE_W = 132;
const TOP_PAD = 64;
const BOTTOM_PAD = 80;

/** Longest-path rank over control edges. Cycles (should not occur in valid flows) degrade to BFS order. */
function computeRanks(nodes: FlowNode[], def: FlowDefinition): Map<string, number> {
  const ids = new Set(nodes.map((n) => n.id));
  const preds = new Map<string, string[]>();
  const succ = new Map<string, string[]>();
  const indeg = new Map<string, number>();
  ids.forEach((id) => {
    preds.set(id, []);
    succ.set(id, []);
    indeg.set(id, 0);
  });
  for (const e of def.edges ?? []) {
    if (!isControlEdge(e) || !ids.has(e.from) || !ids.has(e.to)) continue;
    preds.get(e.to)!.push(e.from);
    succ.get(e.from)!.push(e.to);
    indeg.set(e.to, (indeg.get(e.to) ?? 0) + 1);
  }

  const rank = new Map<string, number>();
  const queue = [...ids].filter((id) => (indeg.get(id) ?? 0) === 0);
  queue.forEach((id) => rank.set(id, 0));

  const workIndeg = new Map(indeg);
  let processed = 0;
  while (queue.length) {
    const n = queue.shift()!;
    processed++;
    const r = rank.get(n) ?? 0;
    for (const m of succ.get(n) ?? []) {
      rank.set(m, Math.max(rank.get(m) ?? 0, r + 1));
      workIndeg.set(m, (workIndeg.get(m) ?? 0) - 1);
      if ((workIndeg.get(m) ?? 0) === 0) queue.push(m);
    }
  }

  // Residual (cyclic) nodes: assign rank = 1 + max control-pred rank, in node order (best effort).
  if (processed < ids.size) {
    for (const n of nodes) {
      if (rank.has(n.id)) continue;
      const pr = (preds.get(n.id) ?? []).map((p) => rank.get(p) ?? 0);
      rank.set(n.id, pr.length ? Math.max(...pr) + 1 : 0);
    }
  }
  return rank;
}

export function layoutFlow(def: FlowDefinition): FlowLayout {
  const nodes = def.nodes ?? [];
  const order = new Map(nodes.map((n, i) => [n.id, i]));
  const rank = computeRanks(nodes, def);

  // Bucket nodes by lane, then by rank, for sibling spread + lane sizing.
  const laneNodes = new Map<string, FlowNode[]>();
  for (const n of nodes) {
    const arr = laneNodes.get(n.stage) ?? [];
    arr.push(n);
    laneNodes.set(n.stage, arr);
  }

  // Max sibling count at any rank, per lane → drives lane width.
  const laneMaxSiblings = new Map<string, number>();
  for (const stage of LIFECYCLE_STAGES) {
    const arr = laneNodes.get(stage.key) ?? [];
    const byRank = new Map<number, number>();
    for (const n of arr) {
      const r = rank.get(n.id) ?? 0;
      byRank.set(r, (byRank.get(r) ?? 0) + 1);
    }
    laneMaxSiblings.set(stage.key, Math.max(1, ...byRank.values(), 0));
  }

  // Lane geometry (all 8 rendered; gate lane slim unless populated).
  const lanes: LaneGeometry[] = [];
  let cursorX = 0;
  for (const stage of LIFECYCLE_STAGES) {
    const count = (laneNodes.get(stage.key) ?? []).length;
    const siblings = laneMaxSiblings.get(stage.key) ?? 1;
    const contentW = siblings * NODE_W + (siblings - 1) * H_GAP + 2 * INNER_PAD;
    const base = stage.isGate && count === 0 ? GATE_LANE_W : BASE_LANE_W;
    const width = Math.max(base, contentW);
    lanes.push({ stage, x: cursorX, width, nodeCount: count, isGate: Boolean(stage.isGate) });
    cursorX += width;
  }
  const laneByStage = new Map(lanes.map((l) => [l.stage.key, l]));

  // Position nodes: Y = global rank band; X = centred sub-columns within the lane.
  const laid: LaidOutNode[] = [];
  let maxRank = 0;
  for (const stage of LIFECYCLE_STAGES) {
    const lane = laneByStage.get(stage.key)!;
    const arr = laneNodes.get(stage.key) ?? [];
    const groups = new Map<number, FlowNode[]>();
    for (const n of arr) {
      const r = rank.get(n.id) ?? 0;
      const g = groups.get(r) ?? [];
      g.push(n);
      groups.set(r, g);
    }
    for (const [r, groupRaw] of groups) {
      const group = [...groupRaw].sort((a, b) => (order.get(a.id) ?? 0) - (order.get(b.id) ?? 0));
      const groupW = group.length * NODE_W + (group.length - 1) * H_GAP;
      const startX = lane.x + (lane.width - groupW) / 2;
      group.forEach((n, i) => {
        laid.push({
          node: n,
          x: startX + i * (NODE_W + H_GAP),
          y: TOP_PAD + r * ROW_H,
          rank: r,
          col: i,
        });
      });
      maxRank = Math.max(maxRank, r);
    }
  }

  return {
    nodes: laid,
    lanes,
    width: cursorX,
    height: TOP_PAD + maxRank * ROW_H + NODE_H + BOTTOM_PAD,
  };
}

export const LAYOUT_CONST = { NODE_W, NODE_H, ROW_H } as const;

/** The lifecycle stage whose lane contains flow x-coordinate `flowX` (for lane-aware drop/placement). */
export function laneAt(layout: FlowLayout, flowX: number): LifecycleStage {
  for (const lane of layout.lanes) {
    if (flowX >= lane.x && flowX < lane.x + lane.width) return lane.stage.key;
  }
  // Left of all lanes → first; right of all → last.
  if (layout.lanes.length > 0 && flowX < layout.lanes[0].x) return layout.lanes[0].stage.key;
  return layout.lanes[layout.lanes.length - 1]?.stage.key ?? 'ONBOARDING';
}
